import React, { useState, useRef, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import InputLabel from '../InputLabel/InputLabel';
import InputError from '../InputError/InputError';

// Helper to generate time intervals
const generateTimeOptions = (intervalMinutes = 15, use12Hour = false) => {
  const options = [];
  const totalMinutes = 24 * 60;
  for (let i = 0; i < totalMinutes; i += intervalMinutes) {
    const hours = Math.floor(i / 60);
    const minutes = i % 60;
    if (use12Hour) {
      const period = hours >= 12 ? 'PM' : 'AM';
      const h12 = hours % 12 || 12;
      const timeString = `${h12}:${minutes.toString().padStart(2, '0')} ${period}`;
      options.push(timeString);
    } else {
      const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      options.push(timeString);
    }
  }
  return options;
};

const TimeField = ({
  label,
  value,
  onChange,
  error,
  name,
  onBlur,
  className = '',
  placeholder = 'HH:mm',
  minTime,
  use12HourFormat = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  
  const timeOptions = useMemo(() => generateTimeOptions(15, use12HourFormat), [use12HourFormat]);

  // Filter options based on current input
  const filteredOptions = useMemo(() => {
    let options = timeOptions;

    // Note: minTime filtering for 12h format is complex as string comparison doesn't work.
    // We'll skip minTime filtering for 12h for now or need to convert to 24h to compare.
    if (minTime && !use12HourFormat) {
      options = options.filter(t => t >= minTime);
    }

    if (showAll) return options;
    if (!value) return options;
    return options.filter(time => time.toLowerCase().includes(value.toLowerCase()));
  }, [value, timeOptions, showAll, minTime, use12HourFormat]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowAll(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    setShowAll(false);
    const inputValue = e.target.value;

    if (use12HourFormat) {
      onChange(inputValue);
      setIsOpen(true);
      return;
    }

    const lastValue = value || '';
    const isDeleting = inputValue.length < lastValue.length;

    let clean = inputValue.replace(/\D/g, '');

    // Auto-pad single digit hour > 2 (e.g. "3" -> "03")
    if (clean.length === 1 && parseInt(clean) > 2) {
      clean = '0' + clean;
    }

    // Validate Hours (00-23)
    if (clean.length >= 2) {
      const hh = parseInt(clean.substring(0, 2));
      if (hh > 23) {
        clean = clean.substring(0, 1);
      }
    }

    // Validate Minutes (00-59)
    if (clean.length >= 4) {
      const mm = parseInt(clean.substring(2, 4));
      if (mm > 59) {
        clean = clean.substring(0, 3);
      }
    }

    // Max length 4 digits
    if (clean.length > 4) {
      clean = clean.substring(0, 4);
    }

    // Format
    let formatted = clean;
    if (clean.length > 2) {
      formatted = clean.substring(0, 2) + ':' + clean.substring(2);
    } else if (clean.length === 2 && !isDeleting) {
      formatted = clean + ':';
    }

    onChange(formatted);
    setIsOpen(true);
  };

  const handleBlur = (e) => {
    if (onBlur) onBlur(e);

    if (use12HourFormat) {
      return;
    }

    // Validate and fix format on blur
    if (value) {
      const parts = value.split(':');
      if (parts.length === 2) {
        let hh = parts[0];
        let mm = parts[1];

        if (hh.length === 1) hh = '0' + hh;
        if (mm.length === 1) mm = '0' + mm;

        // If minutes are missing or empty, default to 00
        if (mm === '') mm = '00';

        const formatted = `${hh}:${mm}`;

        // Final regex check to ensure it's a valid time
        if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formatted)) {
          if (minTime && formatted < minTime) {
            onChange(minTime);
          } else {
            onChange(formatted);
          }
        }
      }
    }
  };

  const handleOptionClick = (time) => {
    onChange(time);
    setIsOpen(false);
    setShowAll(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setShowAll(true);
  };

  const handleButtonClick = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (newState) {
      setShowAll(true);
      // Focus input so blur works correctly
      inputRef.current?.focus();
    }
  };

  return (
    <div className={className} ref={containerRef}>
      {label && <InputLabel htmlFor={name} className="mb-2">{label}</InputLabel>}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={name}
          value={value || ''}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          autoComplete="off"
          className={classNames(
            "block w-full rounded-md bg-white/5 border-0 py-1.5 pl-3 pr-10 text-white shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6",
            error && "ring-red-500 focus:ring-red-500"
          )}
          {...props}
        />
        <button
          type="button"
          onClick={handleButtonClick}
          className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-400 hover:text-white focus:outline-none"
          tabIndex={-1}
          aria-label="Toggle options"
        >
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.76 9.2a.75.75 0 011.06.04l2.7 2.908 2.7-2.908a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 01.04-1.06z" clipRule="evenodd" />
          </svg>
        </button>

        {isOpen && filteredOptions.length > 0 && (
          <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredOptions.map((time) => (
              <li
                key={time}
                className={classNames(
                  "relative cursor-default select-none py-2 pl-3 pr-9 text-white hover:bg-indigo-600 hover:text-white",
                  value === time && "bg-indigo-600"
                )}
                onClick={() => handleOptionClick(time)}
              >
                <span className={classNames("block truncate", value === time && "font-semibold")}>
                  {time}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <InputError className="mt-2">{error}</InputError>}
    </div>
  );
};

export default TimeField;
